"use client";

import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import Image from "next/image";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getJob,
  updateJob,
  listEmployers,
  webImport,
  syncJobStages,
  getGetAtsStageMappingsQueryKey,
} from "@/lib/api/generated";
import type { PipelineStage } from "@/lib/api/types";
import {
  detailsDirty,
  detailsValid,
  stagesDirty,
  mappingDirty,
  buildStageSyncPayload,
  type DetailsSnapshot,
  type StageSnapshot,
  type MappingRecord,
} from "./job-settings-dirty";
import { showToast } from "@/components/ui/sonner";
import {
  isPermissionDenied,
  getPermissionErrorMessage,
} from "@/lib/permissions";
import {
  Sheet,
  SheetHeader,
  SheetTitle,
  SheetClose,
  SheetBody,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button, SubmitButton } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/ui/editor/rich-text-editor";
import { WebImport, WebImportButton } from "@/components/ui/web-import";
import { Select } from "@/components/ui/select";
import { UserSelect } from "@/components/ui/user-select";
import {
  SkeletonContainer,
  SkeletonLabel,
  SkeletonInput,
} from "@/components/ui/skeleton";
import {
  SortableStageList,
  splitStages,
  type StageItem,
} from "@/features/jobs/components/stage-cards";
import { usePipelineViewStore } from "@/features/jobs/store/pipeline/view";
import { Icon } from "@/components/ui/icon";
import { useJobAtsLink } from "@/features/ats-sync/hooks/use-job-ats-link";
import { useStageMappings } from "@/features/ats-sync/hooks/use-ats-job-link";
import { UnlinkConfirmModal } from "@/features/ats-sync/components/unlink-confirm-modal";
import { JobLinkDialog } from "@/features/ats-sync/components/job-link-dialog";
import { IconAvatar } from "@/components/ui/icon-avatar";
import { MAX_JOB_DESCRIPTION_LENGTH } from "@/features/jobs/components/create/constants";
import {
  ApplyJobStagePreset,
  CreatePreset,
  StagePresetActions,
} from "@/features/presets";

interface JobSettingsProps {
  open: boolean;
  onClose: () => void;
  jobId: string | null;
}

function pipelineToStageItem(
  s: PipelineStage,
  mappingOrigin?: string | null,
): StageItem {
  const origin = s.origin_ats_name ?? mappingOrigin ?? undefined;
  return {
    id: s.id,
    name: s.name,
    color: s.color,
    description: s.description ?? "",
    is_system: s.is_system,
    ...(origin ? { ats_name: origin } : {}),
  };
}

/**
 * Project a `StageItem` down to the comparable snapshot used for baseline/diff.
 * `ats_name` is display-only and deliberately dropped so origin enrichment never
 * reads as a stage edit.
 */
function toStageSnapshot(s: StageItem): StageSnapshot {
  return {
    id: s.id,
    name: s.name,
    color: s.color,
    description: s.description ?? "",
    is_system: s.is_system,
  };
}

/**
 * Reconcile local stage drafts against the authoritative `stages` returned by a
 * successful sync. Server ids are canonical, so `custom-*` temp ids collapse to
 * the real UUIDs the backend assigned. Returns the rebuilt list plus a
 * `temp_id → real id` map for the created stages (matched by name) so mapping
 * draft keys can be remapped off their temp ids.
 *
 * `ats_name` (display-only) is preserved by matching the previous draft on id
 * first, then — for freshly-created stages — by name.
 */
function reconcileStagesFromSync(
  responseStages: PipelineStage[],
  prevLocal: StageItem[],
): { next: StageItem[]; tempIdMap: Map<string, string> } {
  const prevById = new Map(prevLocal.map((s) => [s.id, s]));
  // Name → FIFO queue of unconsumed custom stages. A queue (not a single entry)
  // so two freshly-added stages sharing a label each resolve to a DISTINCT
  // temp id: creates are sent, and response stages returned, in order, so we
  // consume same-named customs in that same order rather than collapsing them.
  const prevCustomByName = new Map<string, StageItem[]>();
  for (const s of prevLocal) {
    if (!s.id.startsWith("custom-")) continue;
    const queue = prevCustomByName.get(s.name);
    if (queue) queue.push(s);
    else prevCustomByName.set(s.name, [s]);
  }
  const tempIdMap = new Map<string, string>();

  const next = responseStages.map((rs) => {
    const item = pipelineToStageItem(rs);
    const prevSame = prevById.get(rs.id);
    if (prevSame?.ats_name && !item.ats_name) item.ats_name = prevSame.ats_name;
    if (!prevSame) {
      const oldCustom = prevCustomByName.get(rs.name)?.shift();
      if (oldCustom) {
        tempIdMap.set(oldCustom.id, rs.id);
        if (oldCustom.ats_name && !item.ats_name)
          item.ats_name = oldCustom.ats_name;
      }
    }
    return item;
  });

  return { next, tempIdMap };
}

/** Remap mapping-draft keys off resolved `temp_id → real id` pairs. */
function remapMappingKeys(
  draft: MappingRecord,
  tempIdMap: Map<string, string>,
): MappingRecord {
  if (tempIdMap.size === 0) return { ...draft };
  const remapped: MappingRecord = {};
  for (const [key, value] of Object.entries(draft)) {
    remapped[tempIdMap.get(key) ?? key] = value;
  }
  return remapped;
}

function SectionHeader({ width }: { width: string }) {
  return (
    <div className="flex items-center gap-1.5 px-4 py-3 opacity-[0.96] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] bg-contrast/5">
      <SkeletonInput tone="muted" className="size-4 !rounded" />
      <SkeletonLabel tone="muted" style={{ width }} />
    </div>
  );
}

function JobSettingsSkeleton() {
  return (
    <SkeletonContainer className="flex-col gap-5">
      {/* Details section */}
      <div className="rounded-lg border border-border overflow-clip">
        <SectionHeader width="48px" />
        <div className="flex flex-col gap-5 p-4 bg-surface-header border-t border-border">
          {/* Job Name field */}
          <div className="flex flex-col gap-2">
            <SkeletonLabel className="w-[72px]" />
            <SkeletonInput className="h-9 w-full !rounded-md" />
          </div>
          {/* Job Manager field */}
          <div className="flex flex-col gap-2">
            <SkeletonLabel className="w-[88px]" />
            <SkeletonInput className="h-9 w-full !rounded-md" />
          </div>
          {/* Job Details field */}
          <div className="flex flex-col gap-2">
            <SkeletonLabel className="w-[72px]" />
            <SkeletonInput className="h-[186px] w-full !rounded-md" />
          </div>
        </div>
      </div>

      {/* Stages section */}
      <div className="rounded-lg border border-border overflow-clip">
        <SectionHeader width="44px" />
        <div className="bg-surface-header border-t border-border p-4">
          <div className="flex flex-col gap-3 p-6">
            {[1, 2, 3].map((n) => (
              <div key={n} className="flex items-center gap-3">
                <SkeletonInput tone="muted" className="size-3 !rounded-full" />
                <SkeletonInput className="h-10 flex-1 !rounded-lg" />
              </div>
            ))}
            <SkeletonInput tone="muted" className="h-8 w-28 !rounded-md mt-1" />
          </div>
        </div>
      </div>
    </SkeletonContainer>
  );
}

function EmployerSelect({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const { data, isFetching } = useQuery({
    queryKey: ["employers"],
    queryFn: () => listEmployers({ per_page: 100 }),
  });
  const employers = data?.employers ?? [];
  const selected = employers.find((e) => e.id === value) ?? null;

  const [selectOpen, setSelectOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(timer);
  }, [query]);

  const filtered = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    if (!q) return employers;
    return employers.filter((e) => e.name.toLowerCase().includes(q));
  }, [employers, debouncedQuery]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filtered]);

  useEffect(() => {
    if (!selectOpen) return;
    const timer = setTimeout(() => inputRef.current?.focus(), 10);
    return () => clearTimeout(timer);
  }, [selectOpen]);

  const handleOpenChange = useCallback((next: boolean) => {
    setSelectOpen(next);
    if (!next) {
      setQuery("");
      setDebouncedQuery("");
      setHighlightedIndex(-1);
    }
  }, []);

  const handleSelect = useCallback(
    (id: string) => {
      onChange(id);
      setSelectOpen(false);
      setQuery("");
      setDebouncedQuery("");
      setHighlightedIndex(-1);
    },
    [onChange],
  );

  const showPending = isFetching && employers.length === 0;
  const showEmpty = !showPending && filtered.length === 0;

  return (
    <Select.Root
      open={selectOpen}
      onOpenChange={handleOpenChange}
      highlightedIndex={highlightedIndex}
      onHighlightedIndexChange={setHighlightedIndex}
    >
      <Select.Trigger>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {selected ? (
            <IconAvatar
              src={selected.cover_url}
              size={20}
              className="shrink-0 rounded-[3px]"
              icon={<Icon name="IconBag" className="text-heading opacity-50" />}
            />
          ) : (
            <div className="opacity-40">
              <Icon name="IconBag" size={14} className="text-heading" />
            </div>
          )}
          <span
            className={`flex-1 text-left truncate text-sm ${selected ? "text-heading" : "text-contrast/50"}`}
          >
            {selected?.name ?? "Select employer..."}
          </span>
          <div className="opacity-40">
            <Icon
              name="IconChevronGrabberVertical"
              size={14}
              className="text-heading"
            />
          </div>
        </div>
      </Select.Trigger>

      <Select.Content>
        <Select.Search
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search..."
          loading={isFetching}
        />

        <Select.List>
          {showPending ? (
            <Select.ListPending />
          ) : showEmpty ? (
            <div className="flex flex-col items-center justify-center gap-1 py-8 px-3">
              <div className="opacity-30 mb-1">
                <Icon name="IconBag" size={32} className="text-heading" />
              </div>
            </div>
          ) : (
            filtered.map((emp, i) => {
              const isSelected = emp.id === value;
              return (
                <Select.Item
                  key={emp.id}
                  index={i}
                  selected={isSelected}
                  onSelect={() => handleSelect(emp.id)}
                >
                  <Select.ItemIcon>
                    <IconAvatar
                      src={emp.cover_url}
                      size={20}
                      className="shrink-0 rounded-[3px]"
                      icon={
                        <Icon
                          name="IconBag"
                          className="text-heading opacity-50"
                        />
                      }
                    />
                  </Select.ItemIcon>
                  <Select.ItemLabel selected={isSelected}>
                    {emp.name}
                  </Select.ItemLabel>
                  {isSelected && <Select.ItemCheck />}
                </Select.Item>
              );
            })
          )}
        </Select.List>
      </Select.Content>
    </Select.Root>
  );
}

export function JobSettings({ open, onClose, jobId }: JobSettingsProps) {
  const queryClient = useQueryClient();
  const setSelectedStageId = usePipelineViewStore((s) => s.setSelectedStageId);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [managerId, setManagerId] = useState<string | null>(null);
  const [employerId, setEmployerId] = useState<string | null>(null);
  const [localStages, setLocalStages] = useState<StageItem[]>([]);
  // When apply/save preset sheets are open, hide the settings sheet so only one
  // sheet is visible. Null = settings sheet owns the surface.
  const [presetFlow, setPresetFlow] = useState<"apply" | "save" | null>(null);

  // ── Dirty-section baselines ──
  // Each section (Details / Stages / mapping) is seeded exactly once when its
  // source query first resolves. After seeding, refetches must not clobber the
  // user's in-flight draft; baselines only advance on a successful save. The
  // `*Seeded` refs gate first-time seeding and reset when the sheet closes or
  // the jobId changes so a reopen re-seeds fresh from the server.
  const [detailsBaseline, setDetailsBaseline] =
    useState<DetailsSnapshot | null>(null);
  const [stagesBaseline, setStagesBaseline] = useState<StageSnapshot[]>([]);
  const [mappingBaseline, setMappingBaseline] = useState<MappingRecord>({});
  const detailsSeeded = useRef(false);
  const stagesSeeded = useRef(false);
  const mappingSeeded = useRef(false);
  // True only once Details + Stages baselines have been seeded from the job for
  // THIS open/jobId. Gates the footer so a stale draft from a previous job can
  // never be saved against a freshly-switched jobId before its baseline lands.
  const [hydrated, setHydrated] = useState(false);

  const { data: job, isLoading: jobLoading } = useQuery({
    queryKey: ["jobs", jobId],
    queryFn: () => getJob(jobId!),
    enabled: !!jobId && open,
  });

  // ATS-linked jobs sync their stages with a remote ATS, so stage DELETION is
  // disabled (it would break the mapping) and the add-stage pill is hidden —
  // users map each stage to a remote ATS stage via the card edit panel instead.
  // Only queried while the panel is open.
  const {
    isLinked: atsLinked,
    connectorName,
    dormant,
  } = useJobAtsLink(open ? jobId : null);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  // Shared by the "Connect to ATS" (fresh link) and "Re-link" (dormant resume)
  // footer actions — both open the same JobLinkDialog.
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);

  // Remote-stage mapping (only fetched for a linked job while open). `mapping`
  // holds the pending per-stage remote-stage draft; it's persisted alongside the
  // stage CRUD on Save.
  const { data: stageMapping } = useStageMappings(
    open && atsLinked ? jobId : null,
  );
  const [mappingDraft, setMappingDraft] = useState<
    Record<string, string | null>
  >({});

  // On sheet close or job switch, drop the previous job's drafts AND baselines
  // (not just the seed gates) and mark unhydrated. Otherwise a leftover draft
  // would keep the footer enabled and a click could PATCH/sync stale values
  // against the new jobId before its fresh baseline seeds.
  const prevJobId = useRef(jobId);
  useEffect(() => {
    const jobChanged = prevJobId.current !== jobId;
    prevJobId.current = jobId;
    if (!open || jobChanged) {
      setTitle("");
      setDescription("");
      setManagerId(null);
      setEmployerId(null);
      setLocalStages([]);
      setMappingDraft({});
      setDetailsBaseline(null);
      setStagesBaseline([]);
      setMappingBaseline({});
      detailsSeeded.current = false;
      stagesSeeded.current = false;
      mappingSeeded.current = false;
      setHydrated(false);
      setPresetFlow(null);
    }
  }, [open, jobId]);

  // Seed the mapping baseline+draft once from the stage-mappings query. This is
  // independent of Stages dirtiness: a dirty Stages section must not freeze the
  // first-time mapping seed (job + mappings arrive on separate queries).
  useEffect(() => {
    if (!stageMapping || !open) return;
    if (mappingSeeded.current) return;
    const seed: MappingRecord = {};
    for (const m of stageMapping.mappings) seed[m.stage_id] = m.remote_stage_id;
    setMappingDraft(seed);
    setMappingBaseline(seed);
    mappingSeeded.current = true;
  }, [stageMapping, open]);

  const handleMappingChange = useCallback(
    (stageId: string, remoteStageId: string | null) => {
      setMappingDraft((d) => ({ ...d, [stageId]: remoteStageId }));
    },
    [],
  );

  // Build the per-stage remote-stage selector for the card edit panel. Only
  // editable (non-system, custom_stages tier) stages that already exist server-
  // side are mappable — a brand-new unsaved stage has no id to map yet.
  const mappingEditable = stageMapping?.tier === "custom_stages";
  const renderMapping = useCallback(
    (stage: StageItem) => {
      if (!atsLinked || !stageMapping || !mappingEditable) return undefined;
      if (stage.is_system || stage.id.startsWith("custom-")) return undefined;
      if (!stageMapping.mappings.some((m) => m.stage_id === stage.id))
        return undefined;
      return {
        remoteStages: stageMapping.remote_stages,
        remoteStageId: mappingDraft[stage.id] ?? null,
        connectorName: stageMapping.connector_name,
        onChange: handleMappingChange,
      };
    },
    [
      atsLinked,
      stageMapping,
      mappingEditable,
      mappingDraft,
      handleMappingChange,
    ],
  );

  // Seed Details + Stages baselines once when the job first loads. Once seeded,
  // a refetch does not overwrite the in-flight draft/baseline — baselines only
  // advance on a successful save (or reset on close/job-switch above).
  useEffect(() => {
    if (!job || !open) return;
    if (detailsSeeded.current && stagesSeeded.current) return;

    if (!detailsSeeded.current) {
      const seeded: DetailsSnapshot = {
        title: job.title,
        description: job.description || "",
        managerId: job.job_manager?.id || job.job_manager_id || null,
        employerId: job.employer_id || null,
      };
      setTitle(seeded.title);
      setDescription(seeded.description);
      setManagerId(seeded.managerId);
      setEmployerId(seeded.employerId);
      setDetailsBaseline(seeded);
      detailsSeeded.current = true;
    }

    if (!stagesSeeded.current) {
      const stages = (job.stages ?? []).map((s) => pipelineToStageItem(s));
      setLocalStages(stages);
      setStagesBaseline(stages.map(toStageSnapshot));
      stagesSeeded.current = true;
    }

    setHydrated(true);
  }, [job, open]);

  // Enrich missing origin labels from mapping items once they arrive (stage
  // payload is primary; mapping is a fallback for older/null stage rows).
  useEffect(() => {
    if (!stageMapping || !open) return;
    setLocalStages((prev) => {
      let changed = false;
      const next = prev.map((stage) => {
        if (stage.ats_name) return stage;
        const origin = stageMapping.mappings.find(
          (m) => m.stage_id === stage.id,
        )?.origin_ats_name;
        if (!origin) return stage;
        changed = true;
        return { ...stage, ats_name: origin };
      });
      return changed ? next : prev;
    });
  }, [stageMapping, open]);

  // ── Stage handlers (same logic as wizard) ──

  const handleAddStage = useCallback(
    (name: string, color: string, stageDescription: string) => {
      const id = `custom-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      setLocalStages((prev) => {
        const { leading, editable, trailing } = splitStages(prev);
        return [
          ...leading,
          ...editable,
          { id, name, color, description: stageDescription, is_system: false },
          ...trailing,
        ];
      });
    },
    [],
  );

  const handleUpdateStage = useCallback(
    (id: string, patch: Partial<StageItem>) => {
      setLocalStages((prev) =>
        prev.map((s) => (s.id === id ? { ...s, ...patch } : s)),
      );
    },
    [],
  );

  const handleRemoveStage = useCallback((id: string) => {
    setLocalStages((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const handleReorder = useCallback((reordered: StageItem[]) => {
    setLocalStages(reordered);
  }, []);

  // ── Web import / generate mutations ──

  const titleImportMutation = useMutation({
    mutationFn: (url: string) => webImport({ url, mode: "job_details" }),
    onSuccess: (result) => {
      const importedTitle = result.job_title || result.project_title;
      if (importedTitle) setTitle(importedTitle);
      if (result.description && !description.trim()) {
        setDescription(result.description);
      }
      showToast.success("Imported successfully");
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Failed to import";
      showToast.error("Import failed", { description: message });
    },
  });

  const descImportMutation = useMutation({
    mutationFn: (url: string) => webImport({ url, mode: "job_details" }),
    onSuccess: (result) => {
      if (result.description) {
        setDescription(result.description);
      }
      if (!title.trim()) {
        const importedTitle = result.job_title || result.project_title;
        if (importedTitle) setTitle(importedTitle);
      }
      showToast.success("Description imported");
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Failed to import";
      showToast.error("Import failed", { description: message });
    },
  });

  const handleTitleImport = useCallback(
    async (url: string) => {
      await titleImportMutation.mutateAsync(url);
    },
    [titleImportMutation],
  );

  const handleDescImport = useCallback(
    async (url: string) => {
      await descImportMutation.mutateAsync(url);
    },
    [descImportMutation],
  );

  const handleGenerate = useCallback(() => {
    showToast.info("Coming soon", {
      description: "AI description generation is not available yet.",
    });
  }, []);

  // ── Dirty-section detection (render-time) ──

  // Editable mapping stage ids exclude the immutable system bookends (Sourced
  // pos 0 / Archived pos 999) so a stray bookend draft value never reads dirty.
  const editableMappingStageIds = useMemo(() => {
    if (!stageMapping) return undefined;
    return new Set(
      stageMapping.mappings
        .filter(
          (m) =>
            !(
              m.is_system &&
              (m.stage_position === 0 || m.stage_position === 999)
            ),
        )
        .map((m) => m.stage_id),
    );
  }, [stageMapping]);

  const detailsDraft: DetailsSnapshot = {
    title,
    description,
    managerId,
    employerId,
  };
  const localSnapshots = useMemo(
    () => localStages.map(toStageSnapshot),
    [localStages],
  );

  const isDetailsDirty = detailsBaseline
    ? detailsDirty(detailsBaseline, detailsDraft)
    : false;
  const descriptionLength = description.length;
  const descriptionOverLimit = descriptionLength > MAX_JOB_DESCRIPTION_LENGTH;
  const isDetailsValid = detailsValid(detailsDraft) && !descriptionOverLimit;
  const isStagesDirty = stagesDirty(stagesBaseline, localSnapshots);
  const isMappingDirty = mappingDirty(
    mappingBaseline,
    mappingDraft,
    editableMappingStageIds,
  );

  // Footer gate: Details validity gates the Details write only; Stages/mapping
  // stay independently saveable even while Details is invalid. `hydrated` blocks
  // saving before this job's baselines have been seeded (post open / job switch).
  const footerEnabled =
    hydrated &&
    ((isDetailsDirty && isDetailsValid) || isStagesDirty || isMappingDirty);

  const closePresetFlow = useCallback(() => {
    setPresetFlow(null);
  }, []);

  // Hide settings while a preset sheet is open so only one sheet is on screen.
  const settingsSheetOpen = open && !!jobId && presetFlow === null;

  // ── Save orchestration ──
  //
  // Runs only the dirty section(s). The close decision is computed from LOCAL
  // flags captured here — not the render-time dirty memos — because the baseline
  // `setState` calls below have not flushed by the time onSuccess runs.

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!jobId || !hydrated) return null;

      // Capture the job under save. `jobId` (this closure) is frozen at mutate
      // time; `prevJobId.current` always tracks the CURRENT prop. If the sheet
      // stays open and the prop flips to another job while an await is in
      // flight, post-await state writes must be skipped so they don't land on
      // the new job's state.
      const jobIdAtStart = jobId;
      const jobStillCurrent = () => prevJobId.current === jobIdAtStart;

      const draft: DetailsSnapshot = {
        title,
        description,
        managerId,
        employerId,
      };
      const dDirty = detailsBaseline
        ? detailsDirty(detailsBaseline, draft)
        : false;
      const dValid = detailsValid(draft);
      const snap = localStages.map(toStageSnapshot);
      const sDirty = stagesDirty(stagesBaseline, snap);
      const mDirty = mappingDirty(
        mappingBaseline,
        mappingDraft,
        editableMappingStageIds,
      );
      const detailsStillInvalidDirty = dDirty && !dValid;

      // 1) Details — only when dirty AND valid. A failure here rejects before
      //    Stages sync is attempted (drafts kept, sheet stays open via onError).
      let detailsSaved = false;
      if (dDirty && dValid) {
        await updateJob(jobId, {
          title: title || "Untitled Job",
          description: description || undefined,
          job_manager_id: managerId || undefined,
          employer_id: employerId || undefined,
        });
        // Advance Details baseline immediately so a later Stages failure still
        // reflects the persisted Details (partial cross-section success).
        if (jobStillCurrent()) setDetailsBaseline(draft);
        detailsSaved = true;
      }

      // 2) Stages + mapping — one atomic sync of only the diffed sections.
      let stagesSaved = false;
      if (sDirty || mDirty) {
        const payload = buildStageSyncPayload({
          stagesBaseline,
          localStages: snap,
          mappingBaseline,
          mappingDraft,
          remoteStages: stageMapping?.remote_stages ?? [],
          atsLinked,
        });
        const response = await syncJobStages(jobId, payload);
        // Reconcile from the response so `custom-*` temp ids collapse to the
        // real UUIDs the backend assigned. Without this, a create that lingers
        // in localStages (sheet stayed open on invalid Details) would be sent as
        // a duplicate create on the next save.
        const responseStages = response?.stages ?? [];
        // Skip state writes if the prop flipped to a different job mid-await —
        // otherwise these would overwrite the newly-switched job's state.
        if (jobStillCurrent()) {
          if (responseStages.length > 0) {
            const { next, tempIdMap } = reconcileStagesFromSync(
              responseStages,
              localStages,
            );
            const remappedMapping = remapMappingKeys(mappingDraft, tempIdMap);
            setLocalStages(next);
            setStagesBaseline(next.map(toStageSnapshot));
            setMappingDraft(remappedMapping);
            setMappingBaseline(remappedMapping);
          } else {
            setStagesBaseline(snap);
            setMappingBaseline({ ...mappingDraft });
          }
          setSelectedStageId(null);
        }
        void queryClient.invalidateQueries({
          queryKey: getGetAtsStageMappingsQueryKey(jobId),
        });
        queryClient.removeQueries({ queryKey: ["pipeline", jobId] });
        stagesSaved = true;
      }

      void queryClient.invalidateQueries({ queryKey: ["jobs"] });
      void queryClient.invalidateQueries({ queryKey: ["job", jobId] });
      void queryClient.invalidateQueries({ queryKey: ["jobs", jobId] });

      return {
        dDirty,
        sDirty,
        mDirty,
        detailsSaved,
        stagesSaved,
        detailsStillInvalidDirty,
      };
    },
    onSuccess: (result) => {
      if (!result) return;
      showToast.success("Job settings updated");

      const detailsCleanOrSaved = !result.dDirty || result.detailsSaved;
      const stagesCleanOrSaved = !result.sDirty || result.stagesSaved;
      const mappingCleanOrSaved = !result.mDirty || result.stagesSaved;
      const shouldClose =
        detailsCleanOrSaved &&
        stagesCleanOrSaved &&
        mappingCleanOrSaved &&
        !result.detailsStillInvalidDirty;
      if (shouldClose) onClose();
    },
    onError: (err: Error) => {
      if (isPermissionDenied(err)) {
        showToast.error("Permission denied", {
          description: getPermissionErrorMessage(err),
        });
        onClose();
      } else {
        showToast.error("Update failed", { description: err.message });
      }
    },
  });

  return (
    <>
      <Sheet
        open={settingsSheetOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) onClose();
        }}
      >
        <SheetHeader>
          <div className="flex items-center gap-1.5">
            <Icon name="IconBag" className="size-4" />
            <SheetTitle>Job Settings</SheetTitle>
          </div>
          <SheetClose />
        </SheetHeader>

        <SheetBody>
          {jobLoading || !job ? (
            <JobSettingsSkeleton />
          ) : (
            <div className="flex flex-col gap-5">
              {/* ── Details Section ── */}
              <div className="rounded-lg border border-border overflow-clip">
                <div className="flex items-center gap-1.5 px-4 py-3 opacity-[0.96] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] bg-contrast/5">
                  <Icon
                    name="IconCursorList"
                    size={16}
                    className="text-heading"
                  />
                  <span className="text-sm font-medium text-heading">
                    Details
                  </span>
                </div>

                <div className="flex flex-col gap-5 p-4 bg-surface-header border-t border-border">
                  {/* Job Name */}
                  <div className="flex flex-col gap-2">
                    <Label className="text-heading">Job Name</Label>
                    <div className="relative">
                      <Input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="e.g. Senior Software Engineer"
                        variant="project"
                        className="pr-[122px]"
                      />
                      <div className="absolute right-0 top-0 bottom-0 flex items-center pr-2 z-10">
                        <WebImport
                          onImport={handleTitleImport}
                          isLoading={titleImportMutation.isPending}
                        >
                          <WebImportButton />
                        </WebImport>
                      </div>
                    </div>
                  </div>

                  {/* Job Manager + Employer */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex flex-col gap-2">
                      <Label className="text-heading">Job Manager</Label>
                      <UserSelect
                        value={managerId}
                        onChange={(id) => setManagerId(id)}
                        placeholder="Select manager..."
                        emptyDescription={
                          <span>
                            Invite team members to collaborate on projects.{" "}
                            {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- intentional full-page navigation via window.location after closing the modal, not client-side routing */}
                            <a
                              href="/settings/users"
                              onClick={(e) => {
                                e.preventDefault();
                                onClose();
                                window.location.href = "/settings/users";
                              }}
                              className="text-primary"
                            >
                              Manage users
                            </a>
                          </span>
                        }
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <Label className="text-heading">Employer</Label>
                      <EmployerSelect
                        value={employerId}
                        onChange={(id) => setEmployerId(id)}
                      />
                    </div>
                  </div>

                  {/* Job Details (description) */}
                  <div className="flex flex-col gap-2">
                    <Label className="text-heading">Job Details</Label>
                    <div className="rounded-lg border border-contrast/[0.15] overflow-hidden shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]">
                      <RichTextEditor
                        content={description}
                        onChange={setDescription}
                        placeholder="Describe the role, responsibilities, and key requirements..."
                        wrapperClassName="bg-contrast/6 overflow-hidden"
                        maxHeight="160px"
                      />
                      <div className="flex items-center justify-between bg-contrast/[0.02] border-t border-border p-2">
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={handleGenerate}
                            className="flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity"
                          >
                            <Icon
                              name="IconMagicWand2"
                              size={16}
                              className="text-ai"
                            />
                            <span className="bg-shine text-shine animate-bg-shine text-sm font-medium [--gradient-color:var(--ai)]">
                              Generate
                            </span>
                          </button>
                          <div className="w-px h-[17px] bg-contrast/[0.22]" />
                          <WebImport
                            onImport={handleDescImport}
                            isLoading={descImportMutation.isPending}
                          >
                            <WebImportButton />
                          </WebImport>
                        </div>
                        <button
                          type="button"
                          onClick={() => setDescription("")}
                          className="text-sm text-contrast/50 hover:text-contrast/70 transition-colors cursor-pointer"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-end">
                      <span
                        className={`text-xs leading-4 tabular-nums ${
                          descriptionOverLimit
                            ? "text-destructive"
                            : "text-contrast/40"
                        }`}
                      >
                        {descriptionLength.toLocaleString()} /{" "}
                        {MAX_JOB_DESCRIPTION_LENGTH.toLocaleString()}
                      </span>
                    </div>
                    {descriptionOverLimit && (
                      <p className="text-xs text-destructive leading-4">
                        Description is too long. Please remove{" "}
                        {(
                          descriptionLength - MAX_JOB_DESCRIPTION_LENGTH
                        ).toLocaleString()}{" "}
                        character
                        {descriptionLength - MAX_JOB_DESCRIPTION_LENGTH === 1
                          ? ""
                          : "s"}
                        .
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Stages Section ── */}
              <div className="rounded-lg border border-border overflow-clip">
                <div className="flex items-center justify-between gap-3 px-4 py-3 opacity-[0.96] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] bg-contrast/5">
                  <div className="flex items-center gap-1.5">
                    <Icon name="IconPlan" size={16} className="text-heading" />
                    <span className="text-sm font-medium text-heading">
                      Stages
                    </span>
                  </div>
                  <StagePresetActions
                    stages={localStages}
                    onApplyStages={setLocalStages}
                    onOpenApply={() => setPresetFlow("apply")}
                    onOpenSave={() => setPresetFlow("save")}
                  />
                </div>

                <div className="bg-surface-header border-t border-border p-4">
                  <div className="relative rounded-md">
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 pointer-events-none rounded-md"
                    >
                      <div className="absolute bg-contrast/[0.02] inset-0 rounded-md" />
                      <Image
                        alt=""
                        className="absolute max-w-none mix-blend-screen object-cover opacity-[0.03] rounded-md size-full"
                        src="/assets/grid-bg.png"
                        width={1920}
                        height={1080}
                      />
                    </div>

                    <div className="p-6 relative">
                      <SortableStageList
                        stages={localStages}
                        onUpdate={handleUpdateStage}
                        onRemove={handleRemoveStage}
                        onAdd={handleAddStage}
                        onReorder={handleReorder}
                        atsLinked={atsLinked}
                        renderMapping={renderMapping}
                      />
                    </div>
                  </div>

                  {atsLinked && (
                    <p className="mt-3 text-xs text-contrast/55">
                      Stages sync with {connectorName || "your ATS"}, so they
                      can&apos;t be added or deleted here. Edit a stage to map
                      it to a remote {connectorName || "ATS"} stage.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </SheetBody>

        <SheetFooter>
          {atsLinked && jobId && (
            <Button
              type="button"
              variant="ghost-destructive"
              size="sm"
              className="mr-auto"
              onClick={() => setUnlinkOpen(true)}
            >
              <Icon name="IconBrokenChainLink1" size={14} />
              Unlink from {connectorName || "ATS"}
            </Button>
          )}
          {!atsLinked && dormant && jobId && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="mr-auto text-muted-foreground hover:text-heading"
              onClick={() => setLinkDialogOpen(true)}
            >
              <Icon name="IconBrokenChainLink1" size={14} />
              Re-link to {dormant.connectorName}
            </Button>
          )}
          {!atsLinked && !dormant && jobId && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="mr-auto"
              onClick={() => setLinkDialogOpen(true)}
            >
              <Icon name="IconChainLink1" size={14} />
              Connect to ATS
            </Button>
          )}
          <SubmitButton
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || !footerEnabled}
            loading={saveMutation.isPending}
            loadingLabel="Update Settings"
            variant="primary"
            size="sm"
            className="min-w-32"
          >
            Update Settings
          </SubmitButton>
        </SheetFooter>

        {jobId && (
          <UnlinkConfirmModal
            jobId={jobId}
            connectorName={connectorName}
            open={unlinkOpen}
            onOpenChange={setUnlinkOpen}
          />
        )}
        {jobId && !atsLinked && (
          <JobLinkDialog
            jobId={jobId}
            open={linkDialogOpen}
            onOpenChange={setLinkDialogOpen}
            dormant={
              dormant?.remoteJobId
                ? {
                    connectorName: dormant.connectorName,
                    connectionId: dormant.connectionId,
                    remoteJobId: dormant.remoteJobId,
                  }
                : null
            }
            jobStages={localStages.map((s) => ({ id: s.id, name: s.name }))}
          />
        )}
      </Sheet>

      <ApplyJobStagePreset
        open={presetFlow === "apply"}
        onClose={closePresetFlow}
        currentStages={localStages}
        onApply={setLocalStages}
      />

      <CreatePreset
        open={presetFlow === "save"}
        onClose={closePresetFlow}
        presetType="job-stages"
        initialStages={localStages}
      />
    </>
  );
}
